import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState, } from 'react';
import Webcam from 'react-webcam';
import { Grid, Box, styled, } from '@mui/material';
import CameraSharpIcon from '@mui/icons-material/CameraSharp';
import NoPhotographySharpIcon from '@mui/icons-material/NoPhotographySharp';
import SaveIcon from '@mui/icons-material/Save';
import { Modal } from '../../molecules/Modal';
import Button from '../../atoms/Button';
import { H5, SubText, } from '../../atoms/typography';
import PhotoCard from '../../molecules/UploadedPhotosCard/PhotoCard';
import ImageCaptureOverlay from './ImageCaptureOverlay';
import LivePhotoInstructions from './LivePhotoInstructions';
import ImageCaptureCornerSvg, { IMAGE_CAPTURE_CORNER_POSITION } from './ImageCaptureCornerSvg';
const ErrorImageBox = styled(Box) `
  border: 2px solid ${props => props.theme.palette.error.main};
  background-color: var(--error-states-hover, rgba(211, 47, 47, 0.04));
  border-radius: 4px;
`;
export var UsageType;
(function (UsageType) {
    UsageType["Fraud"] = "fraud";
    UsageType["Flaw"] = "flaw";
    UsageType["Documentation"] = "documentation";
})(UsageType || (UsageType = {}));
const ImageCaptureModal = ({ title, isOpen, onSubmit, onCancel, onClose, images: providedImages, usageType, }) => {
    const [isTakingPhoto, setIsTakingPhoto] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [images, setImages] = useState(providedImages ?? {});
    const [imageErrors, setImageErrors] = useState({});
    const [isCameraAvailable, setIsCameraAvailable] = useState(false);
    const cameraRef = useRef(null);
    const getCameraAvailability = async () => {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasCamera = devices.some(device => device.kind === 'videoinput');
        const cameraPermissions = await navigator.permissions.query({ name: 'camera' });
        if (cameraPermissions.state === 'granted' && !isCameraAvailable && hasCamera) {
            setIsCameraAvailable(true);
        }
        else if (cameraPermissions.state === 'prompt') {
            if (isCameraAvailable) {
                setIsCameraAvailable(false);
            }
            try {
                await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
                setIsCameraAvailable(hasCamera);
            }
            catch {
                setIsCameraAvailable(false);
            }
        }
    };
    useEffect(() => {
        if (isOpen) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            getCameraAvailability();
        }
    }, [isOpen, cameraRef]);
    /**
     * Resets the modal state to the initial state.
     * @param images - The images to reset to. If not provided, it will reset to undefined images.
     */
    const resetImages = (images) => {
        setImages(images ?? {});
    };
    /**
     * This is called when the user clicks cancel on the review step.
     */
    const handleCancel = () => {
        resetImages();
        onCancel();
    };
    /**
   * This is called when the user clicks the X at the top of the modal.
   */
    const handleClose = () => {
        resetImages(providedImages);
        // This looks funny, but its going to call the onCancel if the onClose wasn't provided.
        (onClose ?? onCancel)();
    };
    /**
     * This is the final call when the user clicks the submit button on the review step.
     */
    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await onSubmit(images);
        }
        finally {
            setIsSubmitting(false);
        }
    };
    /**
     * This is called when the user wants to capture a new image.
     */
    const handleTakeImage = useCallback(async () => {
        if (!cameraRef.current)
            return;
        const image = cameraRef.current.getScreenshot();
        if (!image) {
            throw new Error('Could not capture image');
        }
        // Use functional update to avoid stale closure
        setImages((prevImages) => {
            // Find the highest number for the current usage type
            const existingKeys = Object.keys(prevImages);
            // Find all keys that match the usage type pattern (e.g., "fraud_1", "fraud_2")
            const matchingKeys = existingKeys.filter(key => key.startsWith(`${usageType}_`));
            // Extract numbers and find the highest
            let highestNumber = 0;
            matchingKeys.forEach((key) => {
                const match = key.match(/_(\d+)$/);
                if (match) {
                    const num = parseInt(match[1], 10);
                    if (num > highestNumber) {
                        highestNumber = num;
                    }
                }
            });
            // Generate the next key in format: <usage_type>_<number>
            const nextNumber = highestNumber + 1;
            const imageKey = `${usageType}_${nextNumber}`;
            // Return updated images with new image added
            return {
                ...prevImages,
                [imageKey]: image,
            };
        });
        // Clear any errors for the new image
        setImageErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            // Clean up any errors since we're adding a new valid image
            return newErrors;
        });
        // Return to gallery
        setIsTakingPhoto(false);
    }, [usageType]);
    /**
     * This removes an image from the gallery.
     * @param imageKey - The key of the image to remove.
     */
    const handleRemoveImage = (imageKey) => {
        const newImages = { ...images };
        delete newImages[imageKey];
        setImages(newImages);
        // Clear any associated errors
        const newErrors = { ...imageErrors };
        delete newErrors[imageKey];
        setImageErrors(newErrors);
    };
    const instructions = 'Position your item in the center of the frame. Reduce clutter and make sure you have good lighting.';
    /**
     * Video content showing the webcam feed or a message when camera is unavailable.
     */
    const videoContent = isCameraAvailable ? (_jsx(Grid, { item: true, container: true, sx: { backgroundColor: 'action.disabledBackground' }, height: '100%', width: '100%', overflow: 'hidden', borderRadius: 1, alignItems: 'center', justifyContent: 'center', children: _jsx(Webcam, { ref: cameraRef, height: '100%', width: 'auto', audio: false, screenshotFormat: 'image/jpeg', videoConstraints: { height: 800, width: 1200 } }) })) : (_jsxs(Grid, { item: true, container: true, sx: { backgroundColor: 'action.disabledBackground' }, height: '100%', width: '100%', alignItems: 'center', justifyContent: 'center', direction: 'column', rowGap: 1.5, children: [_jsx(Grid, { item: true, children: _jsx(NoPhotographySharpIcon, { sx: { fontSize: '100px', color: 'action.disabled' } }) }), _jsx(Grid, { item: true, children: _jsx(H5, { color: 'action.disabled', children: "No live feed available" }) })] }));
    /**
     * Content displayed when capturing a new image.
     */
    const captureContent = (_jsxs(Grid, { container: true, position: 'relative', width: '100%', height: '800px', children: [videoContent, _jsx(ImageCaptureOverlay, { children: _jsx(Grid, { container: true, height: '100%', sx: { margin: 0, padding: 1 }, direction: 'column', alignItems: 'center', rowSpacing: 1, children: isCameraAvailable ? (_jsxs(_Fragment, { children: [_jsxs(Grid, { item: true, container: true, flex: '1', children: [_jsx(Grid, { item: true, container: true, width: '11%', justifyContent: 'center', alignItems: 'center' }), _jsxs(Grid, { item: true, container: true, width: '78%', position: 'relative', alignItems: 'end', justifyContent: 'center', p: 1, children: [_jsx(LivePhotoInstructions, { prompt: instructions }), _jsx(ImageCaptureCornerSvg, { position: IMAGE_CAPTURE_CORNER_POSITION.TOP_LEFT, size: 40 }), _jsx(ImageCaptureCornerSvg, { position: IMAGE_CAPTURE_CORNER_POSITION.TOP_RIGHT, size: 40 }), _jsx(ImageCaptureCornerSvg, { position: IMAGE_CAPTURE_CORNER_POSITION.BOTTOM_RIGHT, size: 40 }), _jsx(ImageCaptureCornerSvg, { position: IMAGE_CAPTURE_CORNER_POSITION.BOTTOM_LEFT, size: 40 })] }), _jsx(Grid, { item: true, container: true, width: '11%', justifyContent: 'center', alignItems: 'center' })] }), _jsxs(Grid, { item: true, container: true, flexDirection: 'row', columnGap: 1, justifyContent: 'center', alignItems: 'center', children: [_jsx(Grid, { item: true, children: _jsx(Button, { leftIcon: _jsx(CameraSharpIcon, {}), variant: 'contained', color: 'primary', onClick: () => handleTakeImage(), label: `Capture`, size: 'giant' }) }), _jsx(Grid, { item: true, children: _jsx(Button, { size: 'giant', color: 'error', variant: 'contained', label: `Cancel`, onClick: () => setIsTakingPhoto(false) }) })] })] })) : (_jsx(LivePhotoInstructions, { prompt: 'To take a photo, enable camera access in your browser.' })) }) })] }));
    /**
     * This is shown after capturing photos - displays all images in a responsive grid gallery.
     * Only shows images that match the current usageType.
     */
    const filteredImages = Object.entries(images).filter(([key]) => key.startsWith(`${usageType}_`));
    const galleryContent = (_jsx(Grid, { container: true, height: '560px', width: '100%', sx: {
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: 2,
        }, spacing: 2, children: filteredImages.length === 0 ? (_jsxs(Grid, { item: true, xs: 12, container: true, direction: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '500px', children: [_jsx(H5, { color: 'action.disabled', children: "No images captured yet" }), _jsx(SubText, { color: 'action.disabled', children: "Click the camera button to capture images" })] })) : (filteredImages.map(([key, image], idx) => {
            const hasError = !!imageErrors[key];
            const isNewPhoto = image?.startsWith('data:');
            return (_jsx(Grid, { item: true, xs: 12, sm: 6, md: 4, lg: 3, children: _jsxs(Box, { children: [hasError ? (_jsx(ErrorImageBox, { children: _jsx(PhotoCard, { index: idx, url: image || '', onImageDelete: isNewPhoto ? () => handleRemoveImage(key) : undefined }) })) : (_jsx(PhotoCard, { index: idx, url: image || '', onImageDelete: isNewPhoto ? () => handleRemoveImage(key) : undefined })), hasError && (_jsx(Box, { height: '1.5rem', mt: 0.5, children: _jsx(SubText, { color: 'error.main', children: imageErrors[key] }) }))] }) }, key));
        })) }));
    const defaultTitle = `Take ${usageType.charAt(0).toUpperCase()}${usageType.slice(1)} Photos`;
    return _jsx(Modal, { title: title ?? defaultTitle, isOpen: isOpen, content: isTakingPhoto ? captureContent : galleryContent, actions: [
            ...(!isTakingPhoto ? [{
                    label: 'Capture image',
                    variant: 'outlined',
                    leftIcon: _jsx(CameraSharpIcon, {}),
                    onClick: () => setIsTakingPhoto(true),
                    position: 'left',
                }] : []),
            {
                label: 'Cancel',
                variant: 'text',
                onClick: handleCancel,
                disabled: isSubmitting,
            },
            {
                label: 'Save',
                variant: 'contained',
                leftIcon: _jsx(SaveIcon, {}),
                onClick: handleSubmit,
                shortcutKey: 'enter',
                disabled: filteredImages.length === 0,
                isWaiting: isSubmitting,
            },
        ], onClose: handleClose, width: '1200px' });
};
export default ImageCaptureModal;
//# sourceMappingURL=index.js.map